import { edgeVertices, findEdgeIndex, getAdjacencyCache } from "../topology.ts";
import type { PolygonTopology } from "../types.ts";
import { walkRing } from "../walkers.ts";

/**
 * Compute preview positions for an edge loop cut (no topology modification).
 * Returns line-segment pairs as a flat Float32Array [ax,ay,az, bx,by,bz, ...]
 * suitable for THREE.LineSegments (every two consecutive vertices form a segment).
 *
 * Supports multiple cuts: when `cuts > 1`, evenly-spaced cut lines are returned.
 */
export function previewEdgeLoop(
  vertices: Float32Array,
  topology: PolygonTopology,
  seedV0: number,
  seedV1: number,
  cuts: number = 1,
): Float32Array | null {
  const cache = getAdjacencyCache(topology, vertices.length / 3);
  const edgeIdx = findEdgeIndex(topology, seedV0, seedV1);
  if (edgeIdx < 0) return null;
  const loop = walkRing(topology, cache, edgeIdx, 0, vertices);
  if (loop.edges.length === 0) return null;

  const cutCount = Math.max(1, cuts);
  const positions: number[] = [];

  for (let c = 0; c < cutCount; c++) {
    // Compute t value for this cut (evenly spaced, centered)
    let t: number;
    if (cutCount === 1) {
      t = 0.5;
    } else {
      t = (c + 1) / (cutCount + 1);
    }

    // Compute midpoints for all edges at this cut level
    const mids: [number, number, number][] = [];
    for (const ei of loop.edges) {
      const [a, b] = edgeVertices(topology.edges, ei);
      mids.push([
        vertices[a * 3] + (vertices[b * 3] - vertices[a * 3]) * t,
        vertices[a * 3 + 1] + (vertices[b * 3 + 1] - vertices[a * 3 + 1]) * t,
        vertices[a * 3 + 2] + (vertices[b * 3 + 2] - vertices[a * 3 + 2]) * t,
      ]);
    }

    // Emit line segments between consecutive midpoints
    const segCount = loop.closed ? mids.length : mids.length - 1;
    for (let i = 0; i < segCount; i++) {
      const next = (i + 1) % mids.length;
      positions.push(
        mids[i][0],
        mids[i][1],
        mids[i][2],
        mids[next][0],
        mids[next][1],
        mids[next][2],
      );
    }
  }

  return positions.length >= 6 ? new Float32Array(positions) : null;
}
